import { useState, type FormEvent } from "react"
import { Head, Link, router } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { GreenScoreBadge, GreenScoreBreakdown } from "@/components/public/green-score-badge"
import PostcodeMap from "@/components/public/postcode-map"
import CrimeCard from "@/components/public/postcode-report/crime-card"
import PricesCard from "@/components/public/postcode-report/prices-card"
import FloodCard from "@/components/public/postcode-report/flood-card"
import AmenitiesCard from "@/components/public/postcode-report/amenities-card"
import PlanningCard from "@/components/public/postcode-report/planning-card"
import HealthCard from "@/components/public/postcode-report/health-card"
import type { SeoMeta } from "@/types"
import type {
  PostcodeLookup,
  CrimeData,
  LandRegistryData,
  FloodData,
  AmenityData,
  PlanningData,
  HealthData,
  GreenScore,
} from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Raw report shape from Rails (snake_case keys)                       */
/* ------------------------------------------------------------------ */

interface RawReport {
  postcode: {
    postcode: string
    latitude: number
    longitude: number
    admin_district: string
    constituency: string
    lsoa: string | null
    region: string
    outcode?: boolean
  } | null
  crime: {
    total_crimes: number
    month: string
    categories: Array<{ category: string; count: number }>
  } | null
  property: {
    sold_prices: Array<{ price: number; date: string; street: string }>
    price_index: Array<{ date: string; average_price: number }>
    current_average: number | null
  } | null
  flood: {
    stations: Array<{ label: string; river_name?: string; status?: string }>
    station_count: number
    has_active_warnings: boolean
  } | null
  amenities: {
    schools: Array<{ name: string; type?: string }>
    parks: Array<{ name: string }>
    restaurants: Array<{ name: string; cuisine?: string }>
    shops: number
    transport_stops: number
    cycleways: number
  } | null
  planning: {
    conservation_areas: number
    green_belt: boolean | number
    listed_buildings: number
  } | null
  health: {
    life_expectancy_male: number | null
    life_expectancy_female: number | null
    area_name: string
  } | null
  green_score: {
    total: number
    breakdown: {
      green_space: number
      flood_risk: number
      conservation: number
      walkability: number
      cycling: number
    }
  } | null
}

/* ------------------------------------------------------------------ */
/* Mappers: snake_case -> camelCase for card components                 */
/* ------------------------------------------------------------------ */

function mapPostcode(raw: RawReport["postcode"]): PostcodeLookup | null {
  if (!raw) return null
  return {
    postcode: raw.postcode,
    latitude: raw.latitude,
    longitude: raw.longitude,
    adminDistrict: raw.admin_district,
    constituency: raw.constituency,
    lsoa: raw.lsoa,
    region: raw.region,
  }
}

function mapCrime(raw: RawReport["crime"]): CrimeData | null {
  if (!raw) return null
  return {
    totalCrimes: raw.total_crimes,
    month: raw.month,
    categories: raw.categories,
  }
}

function mapProperty(raw: RawReport["property"]): LandRegistryData | null {
  if (!raw) return null
  return {
    soldPrices: raw.sold_prices,
    priceIndex: (raw.price_index ?? []).map((p) => ({
      date: p.date,
      averagePrice: p.average_price,
    })),
    averagePrice: raw.current_average,
  }
}

function mapFlood(raw: RawReport["flood"]): FloodData | null {
  if (!raw) return null
  return {
    stations: raw.stations.map((s) => ({
      label: s.label,
      riverName: s.river_name,
      status: s.status,
    })),
    stationCount: raw.station_count,
    hasActiveWarnings: raw.has_active_warnings,
  }
}

function mapAmenities(raw: RawReport["amenities"]): AmenityData | null {
  if (!raw) return null
  return {
    schools: raw.schools,
    parks: raw.parks,
    restaurants: raw.restaurants,
    shops: raw.shops,
    transportStops: raw.transport_stops,
    cycleways: raw.cycleways,
  }
}

function mapPlanning(raw: RawReport["planning"]): PlanningData | null {
  if (!raw) return null
  return {
    conservationAreas: raw.conservation_areas,
    greenBelt: typeof raw.green_belt === "boolean"
      ? (raw.green_belt ? 1 : 0)
      : raw.green_belt,
    listedBuildings: raw.listed_buildings,
  }
}

function mapHealth(raw: RawReport["health"]): HealthData | null {
  if (!raw) return null
  return {
    lifeExpectancyMale: raw.life_expectancy_male,
    lifeExpectancyFemale: raw.life_expectancy_female,
    areaName: raw.area_name,
  }
}

function mapGreenScore(raw: RawReport["green_score"]): GreenScore | null {
  if (!raw) return null
  return {
    total: raw.total,
    breakdown: {
      greenSpace: raw.breakdown.green_space,
      floodRisk: raw.breakdown.flood_risk,
      conservation: raw.breakdown.conservation,
      walkability: raw.breakdown.walkability,
      cycling: raw.breakdown.cycling,
    },
  }
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

interface PageProps {
  meta: SeoMeta
  postcode: string
  report: RawReport
  formattedAverage: string | null
}

export default function PostcodeReportResultsPage({
  meta,
  postcode,
  report,
  formattedAverage,
}: PageProps) {
  const [searchPostcode, setSearchPostcode] = useState("")
  const [searchLoading, setSearchLoading] = useState(false)

  function handleSearch(e: FormEvent) {
    e.preventDefault()
    const cleaned = searchPostcode.trim().toUpperCase().replace(/\s+/g, "-")
    if (cleaned.length === 0) return
    setSearchLoading(true)
    router.get(`/tools/postcode-report/${cleaned}`, {}, {
      onFinish: () => setSearchLoading(false),
    })
  }

  const postcodeData = mapPostcode(report.postcode)
  const crime = mapCrime(report.crime)
  const property = mapProperty(report.property)
  const flood = mapFlood(report.flood)
  const amenities = mapAmenities(report.amenities)
  const planning = mapPlanning(report.planning)
  const health = mapHealth(report.health)
  const greenScore = mapGreenScore(report.green_score)

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="bg-cream px-6 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <Link
            href="/tools/postcode-report"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-red transition-colors hover:text-deep-red"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
              <path
                fillRule="evenodd"
                d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z"
                clipRule="evenodd"
              />
            </svg>
            Search another postcode
          </Link>

          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
            {postcode}
            <span className="text-red">.</span>
          </h1>

          {postcodeData && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate">
              {postcodeData.adminDistrict && (
                <span>{postcodeData.adminDistrict}</span>
              )}
              {postcodeData.constituency && (
                <span>{postcodeData.constituency}</span>
              )}
              {postcodeData.region && <span>{postcodeData.region}</span>}
            </div>
          )}

          {formattedAverage && (
            <p className="mt-4 text-lg text-charcoal">
              Average property price:{" "}
              <span className="font-display font-bold">
                &pound;{formattedAverage}
              </span>
            </p>
          )}
        </div>
      </section>

      {/* Map */}
      {postcodeData && (
        <section className="border-t border-stone px-6 py-8">
          <div className="mx-auto max-w-5xl">
            <PostcodeMap
              latitude={postcodeData.latitude}
              longitude={postcodeData.longitude}
              postcode={postcodeData.postcode}
              zoom={report.postcode?.outcode ? 12 : 15}
            />
          </div>
        </section>
      )}

      {/* Quick search bar */}
      <section className="border-t border-stone px-6 py-6">
        <div className="mx-auto max-w-5xl">
          <form onSubmit={handleSearch} className="flex max-w-md gap-3">
            <input
              type="text"
              value={searchPostcode}
              onChange={(e) => setSearchPostcode(e.target.value)}
              placeholder="Search another postcode"
              className="flex-1 rounded-lg border border-stone bg-white px-3 py-2 text-sm text-charcoal placeholder:text-slate/50 focus:border-charcoal focus:outline-none"
              aria-label="UK postcode"
            />
            <button
              type="submit"
              disabled={searchLoading}
              className="rounded-lg bg-charcoal px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate disabled:opacity-60"
            >
              {searchLoading ? "Searching..." : "Search"}
            </button>
          </form>
        </div>
      </section>

      {/* Green Score */}
      {greenScore && (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Green Living Score
                  <span className="text-red">.</span>
                </h2>
                <div className="mt-6 flex flex-col items-center gap-8 md:flex-row md:items-start">
                  <GreenScoreBadge score={greenScore} size="lg" />
                  <div className="flex-1">
                    <GreenScoreBreakdown score={greenScore} />
                    <p className="mt-4 text-xs leading-relaxed text-slate">
                      This score combines green spaces (35%), conservation status
                      (20%), walkability (20%), flood resilience (15%), and
                      cycling infrastructure (10%). Scores above 70 are
                      excellent; 40 to 70 is good; below 40 needs improvement.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Property section */}
      {property && (
        <section className="border-t border-stone bg-cream px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <h2 className="font-display text-xl font-bold text-charcoal">
                Property
                <span className="text-red">.</span>
              </h2>
              <p className="mt-2 text-sm text-slate">
                Sold prices recorded by HM Land Registry and the UK House Price
                Index for this area.
              </p>
              <div className="mt-6">
                <PricesCard data={property} />
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Safety section */}
      {crime && (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <h2 className="font-display text-xl font-bold text-charcoal">
                Safety
                <span className="text-red">.</span>
              </h2>
              <p className="mt-2 text-sm text-slate">
                Street-level crime data from Police.uk for the most recent
                available month.
              </p>
              <div className="mt-6">
                <CrimeCard data={crime} />
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Environment section */}
      {(flood || planning) && (
        <section className="border-t border-stone bg-cream px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <h2 className="font-display text-xl font-bold text-charcoal">
                Environment
                <span className="text-red">.</span>
              </h2>
              <p className="mt-2 text-sm text-slate">
                Flood monitoring and planning constraints from the Environment
                Agency and planning.data.gov.uk.
              </p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                {flood && <FloodCard data={flood} />}
                {planning && <PlanningCard data={planning} />}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Neighbourhood section */}
      {(amenities || health) && (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <h2 className="font-display text-xl font-bold text-charcoal">
                Neighbourhood
                <span className="text-red">.</span>
              </h2>
              <p className="mt-2 text-sm text-slate">
                Local amenities from OpenStreetMap and health indicators from PHE
                Fingertips.
              </p>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                {amenities && <AmenitiesCard data={amenities} />}
                {health && <HealthCard data={health} />}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Data sources */}
      <section className="border-t border-stone px-6 py-8">
        <div className="mx-auto max-w-5xl">
          <p className="text-center text-xs text-slate">
            Data sources: postcodes.io, Police.uk, HM Land Registry, Environment Agency,
            OpenStreetMap (Overpass), Planning Data (DLUHC), PHE Fingertips.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Marketing a property here
            <span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Turn your listing into a scroll-stopping video ad. QuiteRight creates
            professional property videos in under 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/features"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See features
            </Link>
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Start free trial
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
